/**
 * Brand config module — white-label seam (UI-v2 Phase 0, Slice 0).
 *
 * Single source of truth for logo / name / favicon / brand id.
 * Components consume brand.* instead of hardcoded strings.
 *
 * Phase 0: only "certain" brand exists. The BrandId union is extended here
 * when white-label tenants are added (Phase 1+). The ThemeProvider reads
 * this at boot to set document.title and favicon, providing the runtime seam.
 *
 * Candidate faces and product copy are NOT brand chrome — they are NOT
 * driven by this module (preserved verbatim per cofounder spec).
 *
 * See: backend/docs/cofounder_brief_ui_v2_theming_architecture.md §5.
 */

/** The set of supported brand IDs. Extend for white-label (Phase 1+). */
export type BrandId = "certain";

export interface BrandConfig {
  /** Unique brand identifier — matches data-brand attribute value. */
  id: BrandId;
  /** Display name for the product (document.title, alt text). */
  name: string;
  /** Logo asset paths. Naming caveat (from /Branding/project_branding.md):
   *   logo-light.png = off-white wordmark → use on DARK backgrounds.
   *   logo-dark.png  = navy wordmark      → use on LIGHT backgrounds.
   *  "logo-{X}" reads as "for use on a {X} background." */
  logo: {
    /** For use on dark backgrounds (off-white rendition). */
    dark: string;
    /** For use on light backgrounds (navy rendition). */
    light: string;
  };
  /** Favicon asset paths (first-paint static HTML remains; boot overrides). */
  favicon: {
    svg: string;
    png: string;
  };
  /** The data-brand attribute value for CSS [data-brand] override blocks. */
  themeAttr: string;
}

/**
 * The active brand configuration.
 *
 * This is the ONLY place these strings live. ThemeProvider reads brand.name
 * and brand.favicon.* at boot to set document.title and favicon hrefs.
 * Wordmark.tsx consumes brand.logo.* and brand.name for the logo img.
 */
export const brand: BrandConfig = {
  id: "certain",
  name: "CertAIn",
  logo: {
    dark: "/logo-light.png",   // off-white wordmark for dark backgrounds
    light: "/logo-dark.png",   // navy wordmark for light backgrounds
  },
  favicon: {
    svg: "/favicon.svg",
    png: "/favicon.png",
  },
  themeAttr: "certain",
};
